import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Privacy Policy — covers POPIA (SA) and adds GDPR-ready wording for EU
 * visitors. Written in plain English; the legally binding bits are called
 * out explicitly.
 */
const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6"
          data-testid="privacy-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>

        <header className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-slate-900" data-testid="privacy-heading">
            Privacy Policy
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Effective date: 28&nbsp;September&nbsp;2026 &middot; Operator (Responsible Party):{' '}
            <strong>Fleet Cost</strong>
          </p>
        </header>

        <article className="prose prose-slate max-w-none space-y-6 text-[15px] leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-900">1. Who is responsible for your data</h2>
            <p>
              <strong>Fleet Cost</strong>, 52 Buffel Str, Hoedspruit, 1380, South Africa is the{' '}
              <strong>Responsible Party</strong> under the Protection of Personal Information Act, 2013
              (POPIA), and the <strong>Data Controller</strong> under the EU General Data Protection
              Regulation (GDPR) where it applies. You can reach our privacy contact at{' '}
              <a className="text-indigo-600 hover:underline" href="mailto:hpbza@icloud.com">hpbza@icloud.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">2. What we collect</h2>
            <p><strong>Account &amp; billing information</strong> — name, email, password (stored as a bcrypt hash), company name, phone number, subscription and payment metadata.</p>
            <p><strong>Fleet data</strong> — vehicle, driver, supplier, cost, incident, licence and document data that you or your team members upload to your tenant.</p>
            <p><strong>Technical data</strong> — IP address, browser + device metadata, log lines, error traces and security events (used to keep the Service up and safe).</p>
            <p><strong>Communications</strong> — emails you send us, support tickets, and delivery receipts for the emails we send you (verification, password reset, digests).</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">3. Why we process it &amp; our lawful basis</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>To provide the Service</strong> &mdash; contract with you (POPIA s.11(1)(b) / GDPR Art&nbsp;6(1)(b)).</li>
              <li><strong>To send transactional emails</strong> (verification, password reset, service alerts, daily digests you opted into) &mdash; contract and, where applicable, your consent.</li>
              <li><strong>To keep the Service secure &amp; prevent abuse</strong> &mdash; legitimate interest (POPIA s.11(1)(f) / GDPR Art&nbsp;6(1)(f)).</li>
              <li><strong>To meet legal obligations</strong> &mdash; tax, accounting, statutory record-keeping (POPIA s.11(1)(c) / GDPR Art&nbsp;6(1)(c)).</li>
              <li><strong>To improve the product</strong> &mdash; aggregate, non-personal analytics; legitimate interest.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">4. Who we share it with</h2>
            <p>We only share personal information with vetted operators (Operators under POPIA / Processors under GDPR) acting under written contract:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Emergent</strong> &mdash; hosting, application platform, database, object storage.</li>
              <li><strong>Resend</strong> &mdash; transactional email delivery.</li>
              <li><strong>Stripe</strong> (when payments are enabled) &mdash; card processing.</li>
              <li>Government, regulators or courts, only when compelled by valid legal process.</li>
            </ul>
            <p>We do <strong>not</strong> sell personal information. We do not use your fleet data to train AI models for other customers.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">5. Where your data lives &amp; cross-border transfers</h2>
            <p>
              We host in cloud regions selected by Emergent. Personal information may be transferred outside
              South Africa or the EEA when we use these Operators. Where we do, we rely on contractual
              safeguards (POPIA s.72 conditions / GDPR Art&nbsp;46 Standard Contractual Clauses) to protect
              your data to an equivalent level.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">6. How long we keep it</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>While your account is active.</li>
              <li>Customer Data for 30 days after account closure so you can export it.</li>
              <li>Billing and tax records for 5 years, or longer if required by law.</li>
              <li>Security logs for up to 12 months.</li>
              <li>Backups purge on rolling 30-day cycles.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">7. Your rights</h2>
            <p>Under POPIA (and GDPR where it applies) you can, subject to limits and verification:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access the personal information we hold about you and get a copy.</li>
              <li>Correct or update inaccurate information.</li>
              <li>Ask us to delete information we no longer need to keep.</li>
              <li>Object to processing based on legitimate interest.</li>
              <li>Withdraw consent for any processing that relies on it (e.g. optional emails).</li>
              <li>Port your data to another service in a machine-readable format.</li>
              <li>Complain to the South African Information Regulator (inforeg@justice.gov.za) or your local EU supervisory authority.</li>
            </ul>
            <p>
              Send requests to <a className="text-indigo-600 hover:underline" href="mailto:hpbza@icloud.com">hpbza@icloud.com</a>{' '}
              and we&rsquo;ll respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">8. Security</h2>
            <p>
              We hash passwords with bcrypt, store auth tokens in httpOnly cookies over TLS, rate-limit login
              and verification attempts, and isolate every tenant&rsquo;s data server-side. No system is
              perfect &mdash; if you spot a security issue, please email{' '}
              <a className="text-indigo-600 hover:underline" href="mailto:hpbza@icloud.com">hpbza@icloud.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">9. Cookies</h2>
            <p>
              We use strictly-necessary cookies to keep you logged in (an httpOnly access-token cookie) and
              a small localStorage flag for user preferences (e.g. &ldquo;exclude private vehicles&rdquo; on
              Reports). We do <strong>not</strong> use advertising or cross-site tracking cookies. Blocking
              essential cookies will prevent the Service from working.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">10. Children</h2>
            <p>
              FleetCost.App is not intended for anyone under 18. Do not create an account or upload personal
              information about minors.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">11. Changes to this Policy</h2>
            <p>
              We may update this Policy. Material changes are announced at least 14 days ahead by email to
              account owners. The &ldquo;Effective date&rdquo; at the top of this page always reflects the
              current version.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">12. Contact &amp; Information Officer</h2>
            <p>
              For any privacy question, POPIA request, or GDPR data-subject request, contact us at{' '}
              <a className="text-indigo-600 hover:underline" href="mailto:hpbza@icloud.com">hpbza@icloud.com</a>{' '}
              or 52 Buffel Str, Hoedspruit, 1380, South Africa. The Information Officer for Fleet Cost is
              the account owner listed on our registration with the Information Regulator.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};

export default PrivacyPage;
